/**
 * date-selekt - date/time picker library, entry point.
 *
 * Usage:
 *   g.dateSelekt('[date-selekt]');    // init all (returns instances)
 *   g.dateSelekt('#from', { ... });     // one element + options
 *   g.dateSelekt.get('#from');          // instance (also: el.dateSelekt)
 *
 * Options resolution (later wins): g.dateSelekt.defaults < init options <
 * `date-selekt-*` element attributes (flat scalars, same rules as selekt:
 * kebab->camelCase, "true"/"false" -> boolean, numbers -> numbers, bare
 * attribute -> true).
 *
 * Depends on genus: g.lang (month/weekday names via Intl), g.translate
 * (interface strings), g.settings.dateFormat / g.settings.timeFormat (the
 * user's DISPLAY format - separate from the language). The wire format from/
 * to the server is always Postgres timestamptz/timetz.
 * Full reference: docs/libs/date-selekt.md.
 */
import { DateSelekt } from './date-selekt.js';

// Bump on every meaningful change to the library.
const VERSION = '0.1.0';

const defaults = {
	type: 'date',          // date | datetime | time
	range: false,          // range variant (creates a second hidden input for the end)
	endName: '',           // range: name of the end field; '' = '<holder name>_to'
	minDate: '',           // PG string, 'today' or 'today±N' - blocks USER picks only
	maxDate: '',
	readonly: false,       // also picked up from the input's readonly attribute
	clearable: true,
	placeholder: '',       // '' = the display format pattern (e.g. DD.MM.YYYY)
	dateFormat: '',        // '' = g.settings.dateFormat (default DD.MM.YYYY)
	timeFormat: '',        // '' = g.settings.timeFormat (default HH:mm)
	minuteStep: 1,
	weekStart: null,       // null = g.settings.weekStart (default 1); 0 = Sunday, 1 = Monday
};

const camel = (s) => s.replace(/-([a-z])/g, (m, c) => c.toUpperCase());

const coerce = (v) => {
	if (v === '') return true;
	if (v === 'true') return true;
	if (v === 'false') return false;
	if (v !== '' && !isNaN(v) && String(Number(v)) === v) return Number(v);
	return v;
};

function attrOptions(el) {
	const opts = {};
	for (const attr of el.attributes) {
		let name = null;
		if (attr.name.startsWith('data-date-selekt-')) name = attr.name.slice(17);
		else if (attr.name.startsWith('date-selekt-')) name = attr.name.slice(12);
		if (!name) continue;
		opts[camel(name)] = coerce(attr.value);
	}
	return opts;
}

function dateSelekt(target, options) {
	let els;
	if (typeof target === 'string') els = Array.from(document.querySelectorAll(target));
	else if (target instanceof Element) els = [target];
	else els = Array.from(target || []);

	const instances = els.map((el) => el.dateSelekt || new DateSelekt(el, { ...defaults, ...options, ...attrOptions(el) }));
	return instances.length === 1 ? instances[0] : instances;
}

dateSelekt.version = () => VERSION;
dateSelekt.defaults = defaults;
dateSelekt.get = (target) => {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	return (el && el.dateSelekt) || null;
};

if (window.g) {
	window.g.dateSelekt = dateSelekt;
	window.g.registerLib('date-selekt');
} else {
	window.dateSelekt = dateSelekt;
}
